import template from './calendar.html'
import * as Utils from '../../lib/savanna_utils.js'
import * as Permissions from '../../lib/permission_utils.js'
import tr from '../../lib/default-translator.js'
import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'

class CalendarViewModel
{
	constructor(page)
	{
		this.page = page;
		this.departments = ko.observableArray();
		this.calendarapplication = ko.observable({});
		this.include_birthdays = ko.observable(false);
		this.include_leave_cycles = ko.observable(true);
		this.include_events = ko.observable(true);
		this.filter = ko.observableArray([]);
		this.loader = ko.observable(false);

		this.include_birthdays.subscribe((state) => {
			this.page.updateData();
		});

		this.include_leave_cycles.subscribe((state) => {
			this.page.updateData();
		});

		this.include_events.subscribe((state) => {
			this.page.updateData();
		});
	}
}

class CalendarPage
{
	constructor(bindings)
	{
		this.bindings = bindings
		this.viewModel = new CalendarViewModel(this)
		this.full_calendar_load()
		window.current_page_title(tr('Calendar'));
	}

	full_calendar_load()
	{
		this.cal = new Calendar(document.getElementById('calendar'), {
			plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
			initialView: 'dayGridMonth',
			headerToolbar: {
				left: 'prev,next today',
				center: 'title',
				right: 'dayGridMonth,timeGridWeek,timeGridDay', // User can switch between week and day views
			},
			events: [],
		})
		this.cal.render();
	}

	async updateData()
	{
		try {
			this.viewModel.loader(true);
			Utils.refresh_data_visible_roles();
			let today = new Date().setHours(0, 0, 0, 0);
			let events = []; // Changed from eventSources to events
			let department_id;

			// Get all department IDs
			let all_department_ids = this.viewModel.departments().map(dept => dept.department_id);

			// Get selected department IDs from filters
			let selected_department_ids = this.viewModel.filter().map(f => f.value);
  
			// If no departments are selected, use all departments
			let department_ids_to_use = selected_department_ids.length > 0 ? selected_department_ids : all_department_ids;

			// Fetch leave cycles
			if (this.viewModel.include_leave_cycles() && await Permissions.checkAccessRights('CalendarViewOwnDepartmentUpcomingLeave'))
			{
				for (let department_id of department_ids_to_use)
				{
					let leave_res = await Grape.fetches.postJSON('/general/get_employees_future_leave_calendar', {
						'employee_id': Utils.current_employee_id(),
						'department_id': department_id
					});

					if (Array.isArray(leave_res))
					{
						events.push(
							...leave_res.map((leave) => {
								let startDate = new Date(leave.date_start);
								let endDate = new Date(leave.date_end);
								if (!isNaN(startDate) && !isNaN(endDate))
								{
									return {
										title: `${leave.firstname} ${leave.surname} (${leave.leave_type})`,
										start: startDate.toISOString().slice(0, 10),
										end: new Date(endDate.getTime() + 86400000).toISOString().slice(0, 10),
										className: 'leave',
										allDay: true,
										icon: leave.icon,
										color: leave.hexcolor
									};
								}
							}).filter(event => event !== undefined)
						);
					}
				}
			}

			// Fetch events
			if (this.viewModel.include_events())
			{
				let events_res = await Grape.fetches.postJSON('/general/get_upcoming_events', {});
				if (Array.isArray(events_res))
				{
					events.push(
						...events_res.map((event) => {
							let eventDate = new Date(event.event_date);
							if (!isNaN(eventDate))
							{
								return {
									title: event.event_name,
									start: eventDate.toISOString().slice(0, 10),
									end: new Date(eventDate.getTime() + 86400000).toISOString().slice(0, 10),
									color: event.hex_colour,
									allDay: true,
								};
							}
						}).filter(event => event !== undefined)
					);
				}
			}

			// Fetch birthdays
			if (this.viewModel.include_birthdays())
			{
				let birthdays_res = await Grape.fetches.postJSON('/general/birthday_list_history', { department_id: department_id });
				if (birthdays_res != null)
				{
					for (let a = 0; a < birthdays_res.length; a++) {
						let birthday = new Date(birthdays_res[a].next_birthday);
						if (!isNaN(birthday))
						{
							let classname = 'birthday ' + ((birthday < today) ? 'history_event' : '');
							events.push({
								title: `${birthdays_res[a].firstname} ${birthdays_res[a].surname}`,
								start: birthday.toISOString().slice(0, 10),
								end: birthday.toISOString().slice(0, 10),
								className: classname,
								allDay: true,
							});
						}
					}
				}
			}

			// Fetch holidays
			let res = await Grape.fetches.postJSON('/leave/get_holidays', {});
			if (res.get_holidays != null)
			{
				for (let a = 0; a < res.get_holidays.length; a++) {
					let holiday = new Date(res.get_holidays[a].holiday_date);
					if (!isNaN(holiday))
					{
						events.push({
							title: ' ' + res.get_holidays[a].description,
							start: holiday.toISOString().slice(0, 10),
							end: holiday.toISOString().slice(0, 10),
							color: res.get_holidays[a].hexcolor,
							allDay: true,
						});
					}
				}
			}
			// Set event sources for FullCalendar
			this.cal.getEvents();
			this.cal.setOption('events', events);
			this.cal.render();
		} catch (error) {
			console.error('Error updating calendar data:', error);
		} finally {
			this.viewModel.loader(false);
		}
	}

	async init()
	{
		document.title = tr('Calendar');

		// check access to page
		if (await Utils.redirectOnAccessDenied('CalendarAccess'))
			return;

		let options = {
			table: 'v_manager_department',
			schema: 'savanna',
			filter: [{
				field: 'employee_id',
				operand: '=',
				value: Utils.current_employee_id()
			}]
		}

		let departments_res = await Grape.fetches.getJSON('/api/record', options);
		let managed_departments = [];

		if (departments_res && departments_res.records)
		{
			managed_departments = [...new Set(departments_res.records.map((record) => ({
				department: record.department,
				department_id: record.department_id,
				company_id: record.company_id,
				company: record.company
				}))
			)];
		}

		this.viewModel.departments(managed_departments);

		this.updateData();
	}

	apply_filter(v)
	{
		let filter = []
		if (v.department_id)
			filter = {
				value: v.department_id,
				operand: '=',
				field: 'department_id',
				name: 'Department',
				filter_remove: false,
				value_name: v.department,
			}
		// check if filter exists
		let exist = this.viewModel.filter().some(f => f.value === filter.value);
		if (v.department && !exist)
		{
			this.viewModel.filter.push(filter);
			this.updateData();
		}
	}
	remove_filter(v)
	{
		this.viewModel.filter.remove(f => f.value === v.value);
		//this.viewModel.filter().remove(v);
		this.updateData();
	}
}

export default {
	route: '[/]general/calendar',
	page_class: CalendarPage,
	template: template,
	template_filename: 'translation/calendar',
}
